/**
 * useGroups Hook
 * Standardized API hooks for group and group membership management
 *
 * Refactored: Phase 1 - Factory Pattern Migration
 * Pattern: Uses createQueryHook and createMutationHook factories for code reduction
 */

import { Group, GroupCreate, GroupUpdate, MemberRole, GroupListResponse, GroupMemberListResponse, GroupMember } from '../../api/data-contracts';
import { log } from '../../utils/errors/logger';
import { QUERY_STALE_TIME } from '../../constants';
import { createQueryHook, createQueryHookNoParams, createMutationHook } from './factories';

/**
 * Response for successful operations
 */
interface SuccessResponse {
  message: string;
}

/**
 * Parameters for adding a member to a group
 */
interface AddGroupMemberParams {
  groupId: string;
  userId: string;
  role?: MemberRole;
}

/**
 * Parameters for removing a member from a group
 */
interface RemoveGroupMemberParams {
  groupId: string;
  userId: string;
}

/**
 * Parameters for updating a group
 */
interface UpdateGroupParams {
  groupId: string;
  data: GroupUpdate;
}

// --- Query Hooks ---

/**
 * Fetches user's groups (groups where current user is a member).
 * Uses /groups/my-groups endpoint for regular users.
 *
 * @param params - Pagination parameters (page, pageSize)
 * @param options - Query options
 * @example
 * // With pagination
 * const { data } = useMyGroups({ page: 1, pageSize: 20 });
 * const groups = data?.groups || [];
 * const pagination = data?.pagination;
 *
 * // Without pagination (backward compatible)
 * const { data: groups } = useMyGroups();
 */
export const useMyGroups = (
  params?: { page?: number; pageSize?: number },
  options?: any
) => {
  const page = params?.page;
  const pageSize = params?.pageSize;

  return createQueryHookNoParams<GroupListResponse>({
    queryKey: () => ['groups', 'my-groups', page, pageSize] as const,
    queryFn: (apiClient) =>
      apiClient.getMyGroupsApiGroupsMyGroupsGet({ page, page_size: pageSize }).then(response => {
        return response.data as GroupListResponse;
      }),
    logContext: 'GET /api/groups/my-groups',
    staleTime: QUERY_STALE_TIME.MEDIUM,
    onSuccess: (data) => {
      log.api.response('GET', '/api/groups/my-groups', 200, {
        groupCount: data?.groups?.length ?? 0,
        total: data?.pagination?.total ?? 0,
      });
    },
  })(options);
};

/**
 * Alternative hook for user's groups (same endpoint, different query key).
 * Kept for backward compatibility.
 */
export const useMyGroupsSimple = createQueryHookNoParams<Group[]>({
  queryKey: () => ['groups', 'my-groups-simple'] as const,
  queryFn: (apiClient) =>
    apiClient.getMyGroupsApiGroupsMyGroupsGet().then(response => {
      const data = response.data as GroupListResponse;
      return data.groups;
    }),
  logContext: 'GET /api/groups/my-groups',
  staleTime: QUERY_STALE_TIME.MEDIUM,
});

/**
 * Fetches all groups for the system (admin/moderator endpoint).
 * Can be disabled based on user permissions via options.enabled.
 *
 * @param params - Pagination parameters (page, pageSize)
 * @param options - Query options (including enabled)
 *
 * @example
 * // With pagination
 * const { data } = useAllGroups({ page: 1, pageSize: 20 });
 * const groups = data?.groups || [];
 * const pagination = data?.pagination;
 *
 * // Backward compatible usage (old signature)
 * const { data } = useAllGroups(undefined, { enabled: hasPermission });
 */
export const useAllGroups = (
  params?: { page?: number; pageSize?: number } | boolean,
  options?: any
) => {
  // Handle backward compatible boolean parameter
  const isOldSignature = typeof params === 'boolean';
  const enabled = isOldSignature ? params : undefined;
  const actualParams = isOldSignature ? undefined : params;
  const actualOptions = isOldSignature ? { enabled } : options;

  const page = actualParams?.page;
  const pageSize = actualParams?.pageSize;

  return createQueryHookNoParams<GroupListResponse>({
    queryKey: () => ['groups', 'all', page, pageSize] as const,
    queryFn: (apiClient) =>
      apiClient.listGroupsApiGroupsGet({ page, page_size: pageSize }).then(response => {
        return response.data as GroupListResponse;
      }),
    logContext: 'GET /api/groups',
    staleTime: QUERY_STALE_TIME.MEDIUM,
    onSuccess: (data) => {
      log.api.response('GET', '/api/groups', 200, {
        groupCount: data?.groups?.length ?? 0,
        total: data?.pagination?.total ?? 0,
      });
    },
  })(actualOptions);
};

/**
 * Fetches members of a specific group with pagination support.
 *
 * @param groupId - The group ID to fetch members for
 * @param params - Pagination parameters (page, pageSize)
 * @param options - Query options (including enabled)
 *
 * @example
 * // With pagination
 * const { data } = useGroupMembers('group-123', { page: 1, pageSize: 20 });
 * const members = data?.members || [];
 * const pagination = data?.pagination;
 *
 * // Backward compatible usage (old signature with boolean)
 * const { data } = useGroupMembers('group-123', undefined, { enabled: isOpen });
 */
export const useGroupMembers = (
  groupId: string,
  params?: { page?: number; pageSize?: number } | boolean,
  options?: any
) => {
  // Handle backward compatible boolean parameter
  const isOldSignature = typeof params === 'boolean';
  const enabled = isOldSignature ? params : undefined;
  const actualParams = isOldSignature ? undefined : params;
  const actualOptions = isOldSignature ? { enabled } : options;

  const page = actualParams?.page;
  const pageSize = actualParams?.pageSize;

  return createQueryHook<GroupMemberListResponse, string>({
    queryKey: (groupId) => ['groups', groupId, 'members', page, pageSize] as const,
    queryFn: (apiClient, groupId) =>
      apiClient.getGroupMembersApiGroupsGroupIdMembersGet(groupId, { page, page_size: pageSize }).then(response => {
        // Backend returns paginated response: { members: [], pagination: {}, summary?: {} }
        return response.data as GroupMemberListResponse;
      }),
    logContext: 'GET /api/groups/:groupId/members',
    staleTime: QUERY_STALE_TIME.MEDIUM,
    onSuccess: (data, groupId) => {
      log.api.response('GET', `/api/groups/${groupId}/members`, 200, {
        memberCount: data?.members?.length ?? 0,
        total: data?.pagination?.total ?? 0,
      });
    },
  })(groupId, actualOptions);
};

// --- Mutation Hooks ---

/**
 * Creates a new group.
 */
export const useCreateGroup = createMutationHook<Group, GroupCreate>({
  mutationFn: (apiClient, newGroupData) =>
    apiClient.createGroupApiGroupsPost(newGroupData).then(response => response.data),
  invalidateQueries: [
    ['groups'],
  ],
  logContext: 'POST /api/groups',
  logMethod: 'POST',
  onSuccess: (data) => {
    log.user.action('group_created', 'useGroups', {
      groupId: data.id,
      groupName: data.displayName
    });
  },
});

/**
 * Updates an existing group.
 */
export const useUpdateGroup = createMutationHook<Group, UpdateGroupParams>({
  mutationFn: (apiClient, { groupId, data }) =>
    apiClient.updateGroupApiGroupsGroupIdPut(groupId, data).then(response => response.data),
  invalidateQueries: [
    ['groups'],
  ],
  logContext: 'PUT /api/groups/:groupId',
  logMethod: 'PUT',
  onSuccess: (data, variables) => {
    log.user.action('group_updated', 'useGroups', {
      groupId: variables.groupId,
      groupName: data.displayName
    });
  },
});

/**
 * Deletes a group.
 */
export const useDeleteGroup = createMutationHook<SuccessResponse, string>({
  mutationFn: (apiClient, groupId) =>
    apiClient.deleteGroupApiGroupsGroupIdDelete(groupId).then(response => response.data),
  invalidateQueries: [
    ['groups'],
  ],
  logContext: 'DELETE /api/groups/:groupId',
  logMethod: 'DELETE',
  onSuccess: (_data, groupId) => {
    log.user.action('group_deleted', 'useGroups', {
      groupId
    });
  },
});

/**
 * Adds a member to a group.
 */
export const useAddGroupMember = createMutationHook<SuccessResponse, AddGroupMemberParams>({
  mutationFn: (apiClient, { groupId, userId, role }) =>
    apiClient.addGroupMemberApiGroupsGroupIdMembersPost(
      groupId,
      { user_id: userId, role }
    ).then(response => response.data),
  invalidateQueries: [
    ['groups'],
  ],
  logContext: 'POST /api/groups/:groupId/members',
  logMethod: 'POST',
  onSuccess: (_data, variables) => {
    log.user.action('group_member_added', 'useGroups', {
      groupId: variables.groupId,
      userId: variables.userId,
      role: variables.role ?? 'not_specified'
    });
  },
});

/**
 * Removes a member from a group.
 */
export const useRemoveGroupMember = createMutationHook<SuccessResponse, RemoveGroupMemberParams>({
  mutationFn: (apiClient, { groupId, userId }) =>
    apiClient.removeGroupMemberApiGroupsGroupIdMembersUserIdDelete(
      groupId,
      userId
    ).then(response => response.data),
  invalidateQueries: [
    ['groups'],
  ],
  logContext: 'DELETE /api/groups/:groupId/members/:userId',
  logMethod: 'DELETE',
  onSuccess: (_data, variables) => {
    log.user.action('group_member_removed', 'useGroups', {
      groupId: variables.groupId,
      userId: variables.userId
    });
  },
});
